import { t } from "tbme/localization";

export const AnalyticEventName = {
  CONTENT_CLICK: "custEv_contentClick",
} as const;

export type AnalyticEventName = typeof AnalyticEventName[keyof typeof AnalyticEventName];

export const AnalyticsPageElement = {
  PROFILE_EDIT: "Profile Edit",
  SOCIAL_ACTION: "Social Action",
  SOCIAL_LINK: "Social Link",
  CAREER_MODE: "Career Mode",
  BADGE_FILTERING: "Badge Filtering",
  COMMUNITY_FEED: "Community Feed",
  FILE_ACCESS: "File Access",
  TRAILHEAD_BUTTON: "Trailhead Button",
} as const;

export type AnalyticsPageElement = typeof AnalyticsPageElement[keyof typeof AnalyticsPageElement];

export const AnalyticsLinkElementType = {
  LINK: "link",
  BUTTON: "button",
} as const;

export type AnalyticsLinkElementType = typeof AnalyticsLinkElementType[keyof typeof AnalyticsLinkElementType];

export interface AnalyticsLink {
  text: string;
  elementType: AnalyticsLinkElementType;
  url?: string;
}

export interface ProfileAnalyticEvent {
  event: AnalyticEventName;
  elementName: AnalyticsPageElement | string;
  link: AnalyticsLink;
}

/**
 * Sends a Profile analytics event to the data layer.
 * @param event - The name of the event to send.
 * @param elementName - The name of the element that was clicked. Usually an
 *   AnalyticsPageElement, but may be a dynamic (e.g. backend-provided) string.
 * @param linkLabel - By default the localization label of the link (E.g
 *   "aboutMe.edit"); the English value of this label is sent to the data layer.
 *   When `useRawLinkText` is true, this value is sent verbatim instead (used for
 *   backend-provided text that has no localization key).
 * @param linkElementType - The type of the link to send.
 * @param linkUrl - The URL of the link to send. Optional.
 * @param useRawLinkText - When true, `linkLabel` is sent as-is without a
 *   localization lookup. Defaults to false.
 * @returns The Profile analytics event that was sent to data layer.
 */
export const sendProfileAnalyticsEvent = (
  event: AnalyticEventName,
  elementName: AnalyticsPageElement | string,
  linkLabel: string,
  linkElementType: AnalyticsLinkElementType,
  linkUrl?: string,
  useRawLinkText: boolean = false
): ProfileAnalyticEvent => {
  const linkText = useRawLinkText ? linkLabel : t(linkLabel, { locale: "en" });

  const profileAnalyticsEvent: ProfileAnalyticEvent = {
    event: event,
    elementName: elementName,
    link: {
      text: linkText,
      elementType: linkElementType,
      ...(linkUrl ? { url: linkUrl } : {}),
    },
  };
  window.dataLayer?.push(profileAnalyticsEvent);
  return profileAnalyticsEvent;
};
